/** Animation type for per-section entrance effects.
 * Each value must have a matching @keyframes in Hero.css AND be handled
 * in getHeroAnimStyle() in Hero.tsx. Adding a new value without CSS = silent no-op. */
export type AnimType = 'fadeInUp' | 'fadeIn' | 'scaleIn' | 'none'

/** Section keys that have configurable animations */
export type SectionKey = 'HERO' | 'ABOUT' | 'EXPERIENCE' | 'ACHIEVEMENTS' | 'PROJECTS' | 'SIDE_PROJECTS' | 'ARCHIVE' | 'EDUCATION' | 'RECOMMENDATIONS' | 'RESUME' | 'FAQ'

/** Master debug configuration. Every toggleable feature, section, project, animation, priority, and order override lives here. Used to build the debug settings panel UI. */
export interface DebugConfig {
  /** Master toggle: enables/disables all debug features */
  ENABLED: boolean

  /** Per-project visibility toggles (true = show, false = hide) */
  PROJECTS: {
    PVP_MULTIPLAYER: boolean
    BUBBLE_SHOOTER_SPACE: boolean
    BIRD_SANCTUARY_3D: boolean
    SUPERHERO_HUNTER: boolean
    STOPWATCH: boolean
    IMAGE_RECOGNITION: boolean
    CSED_VR: boolean
    COLLEGE_PREDICTOR: boolean
    CATAPULT: boolean
  }

  /** Per-side-project visibility toggles (true = show, false = hide) */
  SIDE_PROJECTS: {
    DARQ_REBORN: boolean
    TUFHUB: boolean
    COPYCLIP: boolean
    GRAVITY_BRIDGE: boolean
    CHATALYTICS_PRO: boolean
    MOVIE_ALERT: boolean
    BLUEPRINT_HUB: boolean
  }

  /** Per-section visibility toggles (true = show, false = hide) */
  SECTIONS: {
    HERO: boolean
    ABOUT: boolean
    EXPERIENCE: boolean
    ACHIEVEMENTS: boolean
    PROJECTS: boolean
    SIDE_PROJECTS: boolean
    ARCHIVE: boolean
    EDUCATION: boolean
    RECOMMENDATIONS: boolean
    RESUME: boolean
    FAQ: boolean
    FOOTER: boolean
  }

  /** Recommendations section layout: 0 = carousel, 1 = masonry grid */
  RECOMMENDATIONS_LAYOUT: 0 | 1
  /** Achievements section layout: 0 = sticky card stack, 1 = timeline */
  ACHIEVEMENTS_LAYOUT: 0 | 1
  /** Lazy loading strategy: 0 = timer-based priority, 1 = IntersectionObserver, 2 = background preload */
  LAZY_LOADING_STRATEGY: 0 | 1 | 2

  /** Show/hide the custom animated cursor on desktop */
  CUSTOM_CURSOR: boolean
  /** Show/hide the animated canvas particle background */
  CANVAS_BACKGROUND: boolean
  /** Show/hide GitHub contribution stats widget in the hero section */
  GITHUB_STATS: boolean
  /** Show/hide developer comment easter eggs next to section titles */
  DEV_COMMENTS: boolean
  /** MA logo hover effect: 0 = none, 1 = coder peek, 2 = wave ripple */
  MA_HOVER_EFFECT: 0 | 1 | 2
  /** Dev mode auto-lock timer duration in minutes. Options: 0.5 (30s), 1, 5, 10, 15, 30 */
  DEV_MODE_TIMER: number
  /** When true, Experience timeline items use spring physics instead of tween for entrance animation */
  EXPERIENCE_SPRING_ANIM: boolean
  /** Master toggle for all Framer Motion scroll animations */
  ANIMATIONS: boolean

  /** Per-section animation toggles. Only effective when ANIMATIONS is enabled. */
  ANIM_SECTIONS: {
    HERO: boolean
    ABOUT: boolean
    EXPERIENCE: boolean
    ACHIEVEMENTS: boolean
    PROJECTS: boolean
    SIDE_PROJECTS: boolean
    ARCHIVE: boolean
    EDUCATION: boolean
    RECOMMENDATIONS: boolean
    RESUME: boolean
    FAQ: boolean
  }

  /** Per-section animation type (fadeInUp, fadeIn, scaleIn, none). Controls which entrance animation plays for each section. */
  ANIM_TYPE: Record<SectionKey, AnimType>

  /** Per-section animation speed in seconds (0.2 - 2.0). Controls how long each section's entrance animation takes. */
  ANIM_SPEED: Record<SectionKey, number>

  /** When true, custom cursor loads eagerly with the main bundle; when false, loads as a lazy chunk */
  CURSOR_EAGER_LOAD: boolean

  /** Loading priority values (0 = highest priority, 99 = lowest). Priority 0-1 loads eagerly; 2+ loads as separate chunk. */
  PRIORITY: {
    PVP_MULTIPLAYER: number
    BUBBLE_SHOOTER_SPACE: number
    BIRD_SANCTUARY_3D: number
    SUPERHERO_HUNTER: number
    STOPWATCH: number
    IMAGE_RECOGNITION: number
    CSED_VR: number
    COLLEGE_PREDICTOR: number
    CATAPULT: number
    HERO: number
    ABOUT: number
    EXPERIENCE: number
    ACHIEVEMENTS: number
    PROJECTS: number
    SIDE_PROJECTS: number
    ARCHIVE: number
    EDUCATION: number
    RECOMMENDATIONS: number
    RESUME: number
    FAQ: number
    FOOTER: number
    CUSTOM_CURSOR: number
    CANVAS_BACKGROUND: number
  }

  /** Custom order overrides for sections and projects. Null means use the default static order. */
  ORDER: {
    SECTIONS: string[] | null
    PROJECTS: string[] | null
    SIDE_PROJECTS: string[] | null
  }
}

/** Baseline configuration used in production (all features enabled). Overridden by persisted debug state when running on localhost. Ensures the production build always starts from a known good state. */
const PRODUCTION_DEFAULTS: DebugConfig = {
  ENABLED: false,
  PROJECTS: {
    PVP_MULTIPLAYER: true,
    BUBBLE_SHOOTER_SPACE: true,
    BIRD_SANCTUARY_3D: true,
    SUPERHERO_HUNTER: true,
    STOPWATCH: true,
    IMAGE_RECOGNITION: true,
    CSED_VR: true,
    COLLEGE_PREDICTOR: true,
    CATAPULT: true,
  },
  SIDE_PROJECTS: {
    DARQ_REBORN: true,
    TUFHUB: true,
    COPYCLIP: true,
    GRAVITY_BRIDGE: true,
    CHATALYTICS_PRO: true,
    MOVIE_ALERT: true,
    BLUEPRINT_HUB: true,
  },
  SECTIONS: {
    HERO: true,
    ABOUT: true,
    EXPERIENCE: true,
    ACHIEVEMENTS: true,
    PROJECTS: true,
    // SIDE_PROJECTS/ARCHIVE built 2026-08-30 for a Bikram-agarwal-style showcase, then redirected to
    // Arora-Sir.github.io instead (the right analog to a username.github.io site). Code stays intact -
    // flip these back to true to re-enable if this ever belongs here after all.
    SIDE_PROJECTS: false,
    ARCHIVE: false,
    EDUCATION: true,
    RECOMMENDATIONS: true,
    RESUME: true,
    FAQ: true,
    FOOTER: true,
  },
  RECOMMENDATIONS_LAYOUT: 0,
  ACHIEVEMENTS_LAYOUT: 0,
  LAZY_LOADING_STRATEGY: 2,
  CUSTOM_CURSOR: true,
  CANVAS_BACKGROUND: true,
  GITHUB_STATS: true,
  DEV_COMMENTS: true,
  MA_HOVER_EFFECT: 1,
  DEV_MODE_TIMER: 15,
  EXPERIENCE_SPRING_ANIM: true,
  ANIMATIONS: true,
  ANIM_SECTIONS: {
    HERO: true,
    ABOUT: true,
    EXPERIENCE: true,
    ACHIEVEMENTS: true,
    PROJECTS: true,
    SIDE_PROJECTS: true,
    ARCHIVE: true,
    EDUCATION: true,
    RECOMMENDATIONS: true,
    RESUME: true,
    FAQ: true,
  },
  ANIM_TYPE: {
    HERO: 'fadeInUp',
    ABOUT: 'fadeIn',
    EXPERIENCE: 'fadeInUp',
    ACHIEVEMENTS: 'fadeInUp',
    PROJECTS: 'fadeInUp',
    SIDE_PROJECTS: 'fadeInUp',
    ARCHIVE: 'fadeIn',
    EDUCATION: 'fadeIn',
    RECOMMENDATIONS: 'fadeInUp',
    RESUME: 'fadeInUp',
    FAQ: 'fadeInUp',
  },
  ANIM_SPEED: {
    HERO: 0.5,
    ABOUT: 0.6,
    EXPERIENCE: 0.6,
    ACHIEVEMENTS: 0.5,
    PROJECTS: 0.5,
    SIDE_PROJECTS: 0.5,
    ARCHIVE: 0.6,
    EDUCATION: 0.6,
    RECOMMENDATIONS: 0.5,
    RESUME: 0.5,
    FAQ: 0.5,
  },
  CURSOR_EAGER_LOAD: false,
  PRIORITY: {
    PVP_MULTIPLAYER: 0,
    BUBBLE_SHOOTER_SPACE: 0,
    BIRD_SANCTUARY_3D: 0,
    SUPERHERO_HUNTER: 99,
    STOPWATCH: 99,
    IMAGE_RECOGNITION: 99,
    CSED_VR: 99,
    COLLEGE_PREDICTOR: 99,
    CATAPULT: 99,
    HERO: 0,
    ABOUT: 0,
    EXPERIENCE: 0,
    PROJECTS: 0,
    ACHIEVEMENTS: 1,
    RECOMMENDATIONS: 2,
    RESUME: 3,
    EDUCATION: 4,
    FAQ: 5,
    SIDE_PROJECTS: 6,
    ARCHIVE: 7,
    FOOTER: 99,
    CUSTOM_CURSOR: 1,
    CANVAS_BACKGROUND: 2,
  },
  ORDER: {
    SECTIONS: null,
    PROJECTS: null,
    SIDE_PROJECTS: null,
  },
}

/** Checks if the app is running on localhost (including IPv4 and IPv6 loopback). Returns false during SSR when window is undefined. Used to gate debug features to dev only. */
export function isLocalhost(): boolean {
  if (typeof window === 'undefined') return false
  const host = window.location.hostname
  return host === 'localhost' || host === '127.0.0.1' || host === '[::1]'
}

/** localStorage key used to persist and retrieve debug toggle state across sessions */
export const STORAGE_KEY = 'ma_debug_overrides'

/** Partial version of DebugConfig where every field is optional. Enables incremental overrides from localStorage without needing to specify the entire config. */
type Overrides = {
  [K in keyof DebugConfig]?: DebugConfig[K] extends object
    ? { [P in keyof DebugConfig[K]]?: DebugConfig[K][P] }
    : DebugConfig[K]
}

/** Reads persisted debug overrides from localStorage. Returns null if no overrides exist or if JSON parsing fails. Swallows errors silently. */
function loadOverrides(): Overrides | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as Overrides
  } catch {
    return null
  }
}

/** Deep-merges partial override values into the base config. Each subsection (PROJECTS, SECTIONS, ANIM_SECTIONS, ANIM_TYPE, ANIM_SPEED, PRIORITY, ORDER) is spread separately so unspecified fields retain their base values. */
export function applyOverrides(base: DebugConfig, overrides: Overrides): DebugConfig {
  return {
    ...base,
    PROJECTS: { ...base.PROJECTS, ...overrides.PROJECTS },
    SIDE_PROJECTS: { ...base.SIDE_PROJECTS, ...overrides.SIDE_PROJECTS },
    SECTIONS: { ...base.SECTIONS, ...overrides.SECTIONS },
    RECOMMENDATIONS_LAYOUT: overrides.RECOMMENDATIONS_LAYOUT ?? base.RECOMMENDATIONS_LAYOUT,
    ACHIEVEMENTS_LAYOUT: overrides.ACHIEVEMENTS_LAYOUT ?? base.ACHIEVEMENTS_LAYOUT,
    LAZY_LOADING_STRATEGY: overrides.LAZY_LOADING_STRATEGY ?? base.LAZY_LOADING_STRATEGY,
    CUSTOM_CURSOR: overrides.CUSTOM_CURSOR ?? base.CUSTOM_CURSOR,
    CANVAS_BACKGROUND: overrides.CANVAS_BACKGROUND ?? base.CANVAS_BACKGROUND,
    GITHUB_STATS: overrides.GITHUB_STATS ?? base.GITHUB_STATS,
    DEV_COMMENTS: overrides.DEV_COMMENTS ?? base.DEV_COMMENTS,
    MA_HOVER_EFFECT: overrides.MA_HOVER_EFFECT ?? base.MA_HOVER_EFFECT,
    DEV_MODE_TIMER: overrides.DEV_MODE_TIMER ?? base.DEV_MODE_TIMER,
    EXPERIENCE_SPRING_ANIM: overrides.EXPERIENCE_SPRING_ANIM ?? base.EXPERIENCE_SPRING_ANIM,
    ANIMATIONS: overrides.ANIMATIONS ?? base.ANIMATIONS,
    ANIM_SECTIONS: {
      HERO: overrides.ANIM_SECTIONS?.HERO ?? base.ANIM_SECTIONS?.HERO ?? true,
      ABOUT: overrides.ANIM_SECTIONS?.ABOUT ?? base.ANIM_SECTIONS?.ABOUT ?? true,
      EXPERIENCE: overrides.ANIM_SECTIONS?.EXPERIENCE ?? base.ANIM_SECTIONS?.EXPERIENCE ?? true,
      ACHIEVEMENTS: overrides.ANIM_SECTIONS?.ACHIEVEMENTS ?? base.ANIM_SECTIONS?.ACHIEVEMENTS ?? true,
      PROJECTS: overrides.ANIM_SECTIONS?.PROJECTS ?? base.ANIM_SECTIONS?.PROJECTS ?? true,
      SIDE_PROJECTS: overrides.ANIM_SECTIONS?.SIDE_PROJECTS ?? base.ANIM_SECTIONS?.SIDE_PROJECTS ?? true,
      ARCHIVE: overrides.ANIM_SECTIONS?.ARCHIVE ?? base.ANIM_SECTIONS?.ARCHIVE ?? true,
      EDUCATION: overrides.ANIM_SECTIONS?.EDUCATION ?? base.ANIM_SECTIONS?.EDUCATION ?? true,
      RECOMMENDATIONS: overrides.ANIM_SECTIONS?.RECOMMENDATIONS ?? base.ANIM_SECTIONS?.RECOMMENDATIONS ?? true,
      RESUME: overrides.ANIM_SECTIONS?.RESUME ?? base.ANIM_SECTIONS?.RESUME ?? true,
      FAQ: overrides.ANIM_SECTIONS?.FAQ ?? base.ANIM_SECTIONS?.FAQ ?? true,
    },
    ANIM_TYPE: { ...base.ANIM_TYPE, ...overrides.ANIM_TYPE },
    ANIM_SPEED: { ...base.ANIM_SPEED, ...overrides.ANIM_SPEED },
    CURSOR_EAGER_LOAD: overrides.CURSOR_EAGER_LOAD ?? base.CURSOR_EAGER_LOAD ?? true,
    PRIORITY: {
      PVP_MULTIPLAYER: overrides.PRIORITY?.PVP_MULTIPLAYER ?? base.PRIORITY?.PVP_MULTIPLAYER ?? 0,
      BUBBLE_SHOOTER_SPACE: overrides.PRIORITY?.BUBBLE_SHOOTER_SPACE ?? base.PRIORITY?.BUBBLE_SHOOTER_SPACE ?? 0,
      BIRD_SANCTUARY_3D: overrides.PRIORITY?.BIRD_SANCTUARY_3D ?? base.PRIORITY?.BIRD_SANCTUARY_3D ?? 0,
      SUPERHERO_HUNTER: overrides.PRIORITY?.SUPERHERO_HUNTER ?? base.PRIORITY?.SUPERHERO_HUNTER ?? 99,
      STOPWATCH: overrides.PRIORITY?.STOPWATCH ?? base.PRIORITY?.STOPWATCH ?? 99,
      IMAGE_RECOGNITION: overrides.PRIORITY?.IMAGE_RECOGNITION ?? base.PRIORITY?.IMAGE_RECOGNITION ?? 99,
      CSED_VR: overrides.PRIORITY?.CSED_VR ?? base.PRIORITY?.CSED_VR ?? 99,
      COLLEGE_PREDICTOR: overrides.PRIORITY?.COLLEGE_PREDICTOR ?? base.PRIORITY?.COLLEGE_PREDICTOR ?? 99,
      CATAPULT: overrides.PRIORITY?.CATAPULT ?? base.PRIORITY?.CATAPULT ?? 99,
      HERO: overrides.PRIORITY?.HERO ?? base.PRIORITY?.HERO ?? 0,
      ABOUT: overrides.PRIORITY?.ABOUT ?? base.PRIORITY?.ABOUT ?? 0,
      EXPERIENCE: overrides.PRIORITY?.EXPERIENCE ?? base.PRIORITY?.EXPERIENCE ?? 0,
      PROJECTS: overrides.PRIORITY?.PROJECTS ?? base.PRIORITY?.PROJECTS ?? 0,
      ACHIEVEMENTS: overrides.PRIORITY?.ACHIEVEMENTS ?? base.PRIORITY?.ACHIEVEMENTS ?? 1,
      RECOMMENDATIONS: overrides.PRIORITY?.RECOMMENDATIONS ?? base.PRIORITY?.RECOMMENDATIONS ?? 2,
      RESUME: overrides.PRIORITY?.RESUME ?? base.PRIORITY?.RESUME ?? 3,
      EDUCATION: overrides.PRIORITY?.EDUCATION ?? base.PRIORITY?.EDUCATION ?? 4,
      FAQ: overrides.PRIORITY?.FAQ ?? base.PRIORITY?.FAQ ?? 5,
      SIDE_PROJECTS: overrides.PRIORITY?.SIDE_PROJECTS ?? base.PRIORITY?.SIDE_PROJECTS ?? 6,
      ARCHIVE: overrides.PRIORITY?.ARCHIVE ?? base.PRIORITY?.ARCHIVE ?? 7,
      FOOTER: overrides.PRIORITY?.FOOTER ?? base.PRIORITY?.FOOTER ?? 99,
      CUSTOM_CURSOR: overrides.PRIORITY?.CUSTOM_CURSOR ?? base.PRIORITY?.CUSTOM_CURSOR ?? 1,
      CANVAS_BACKGROUND: overrides.PRIORITY?.CANVAS_BACKGROUND ?? base.PRIORITY?.CANVAS_BACKGROUND ?? 2,
    },
    ORDER: {
      SECTIONS: overrides.ORDER?.SECTIONS ?? base.ORDER?.SECTIONS ?? null,
      PROJECTS: overrides.ORDER?.PROJECTS ?? base.ORDER?.PROJECTS ?? null,
      SIDE_PROJECTS: overrides.ORDER?.SIDE_PROJECTS ?? base.ORDER?.SIDE_PROJECTS ?? null,
    },
  }
}

/** Constructs the effective debug config for the current environment. Returns production defaults with ENABLED=false when not on localhost. On localhost, starts from defaults with ENABLED=true and applies any persisted localStorage overrides. */
export function buildDebugConfig(): DebugConfig {
  if (!isLocalhost()) {
    return { ...PRODUCTION_DEFAULTS, ENABLED: false }
  }

  const base: DebugConfig = {
    ...PRODUCTION_DEFAULTS,
    ENABLED: true,
  }

  const overrides = loadOverrides()
  if (overrides) {
    return applyOverrides(base, overrides)
  }

  return base
}

/** Persists the current debug config to localStorage as a partial overrides object. Only the top-level fields needed for restoration are saved. Silent on storage errors. */
export function persistDebugState(config: DebugConfig): void {
  try {
    const overrides: Overrides = {
      PROJECTS: config.PROJECTS,
      SIDE_PROJECTS: config.SIDE_PROJECTS,
      SECTIONS: config.SECTIONS,
      RECOMMENDATIONS_LAYOUT: config.RECOMMENDATIONS_LAYOUT,
      ACHIEVEMENTS_LAYOUT: config.ACHIEVEMENTS_LAYOUT,
      LAZY_LOADING_STRATEGY: config.LAZY_LOADING_STRATEGY,
      CUSTOM_CURSOR: config.CUSTOM_CURSOR,
      CANVAS_BACKGROUND: config.CANVAS_BACKGROUND,
      GITHUB_STATS: config.GITHUB_STATS,
      DEV_COMMENTS: config.DEV_COMMENTS,
      MA_HOVER_EFFECT: config.MA_HOVER_EFFECT,
      DEV_MODE_TIMER: config.DEV_MODE_TIMER,
      EXPERIENCE_SPRING_ANIM: config.EXPERIENCE_SPRING_ANIM,
      ANIMATIONS: config.ANIMATIONS,
      ORDER: config.ORDER,
    ANIM_SECTIONS: { ...config.ANIM_SECTIONS },
    ANIM_TYPE: { ...config.ANIM_TYPE } as Record<SectionKey, AnimType>,
    ANIM_SPEED: { ...config.ANIM_SPEED } as Record<SectionKey, number>,
      CURSOR_EAGER_LOAD: config.CURSOR_EAGER_LOAD,
      PRIORITY: config.PRIORITY,
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides))
  } catch {
    // silent
  }
}

/** Removes all persisted debug overrides from localStorage, resetting to production defaults on the next config build. */
export function clearDebugState(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // silent
  }
}

/** Metadata shape for each toggle in the debug settings panel. Describes what the toggle controls, its performance implications, UX tradeoffs, and recommended value. */
export interface DebugToggleInfo {
  /** Human-readable label shown in the debug panel */
  label: string
  /** Short description of what this toggle controls */
  description: string
  /** Estimated performance cost: Low, Medium, or High */
  performanceImpact: 'Low' | 'Medium' | 'High'
  /** What the user sacrifices or gains by changing this toggle */
  uxTradeoff: string
  /** Default or recommended value for this toggle */
  recommended: boolean | number | string
  /** Benefit of enabling this feature */
  gain: string
  /** Downside of enabling this feature */
  lose: string
}

/** Registry of metadata for every debug toggle, keyed by dot-path (e.g. "PROJECTS.PVP_MULTIPLAYER"). Consumed by the debug panel to render labeled, documented toggle controls with UX guidance. */
export const DEBUG_INFO: Record<string, DebugToggleInfo> = {
  'PROJECTS.PVP_MULTIPLAYER': {
    label: 'PVP Multiplayer',
    description: 'Show/hide the PvP Multiplayer Match Engine project card.',
    performanceImpact: 'Low',
    uxTradeoff: 'Less projects to scroll through',
    recommended: true,
    gain: 'Highlights senior-level backend work',
    lose: 'Fewer total projects displayed',
  },
  'PROJECTS.BUBBLE_SHOOTER_SPACE': {
    label: 'Bubble Shooter Space',
    description: 'Show/hide the Bubble Shooter Space project card.',
    performanceImpact: 'Low',
    uxTradeoff: 'Less projects to scroll through',
    recommended: true,
    gain: 'Shows top-grossing mobile game experience',
    lose: 'Fewer total projects displayed',
  },
  'PROJECTS.BIRD_SANCTUARY_3D': {
    label: '3D Bird Sanctuary',
    description: 'Show/hide the 3D Bird Sanctuary internal project card.',
    performanceImpact: 'Low',
    uxTradeoff: 'Hiding internal IP reduces clutter',
    recommended: true,
    gain: 'Visitors see your best work first',
    lose: 'Hidden behind company IP badge',
  },
  'PROJECTS.SUPERHERO_HUNTER': {
    label: 'SuperHero Hunter',
    description: 'Show/hide the SuperHero Hunter SPA project.',
    performanceImpact: 'Low',
    uxTradeoff: 'Less projects to scroll through',
    recommended: true,
    gain: 'Shows frontend SPA skills',
    lose: 'Fewer total projects displayed',
  },
  'PROJECTS.STOPWATCH': {
    label: 'StopWatch',
    description: 'Show/hide the StopWatch utility project.',
    performanceImpact: 'Low',
    uxTradeoff: 'Less projects to scroll through',
    recommended: true,
    gain: 'Shows zero-dep vanilla JS work',
    lose: 'Fewer total projects displayed',
  },
  'PROJECTS.IMAGE_RECOGNITION': {
    label: 'Image Recognition',
    description: 'Show/hide the Image Recognition Attendance project.',
    performanceImpact: 'Low',
    uxTradeoff: 'Less projects to scroll through',
    recommended: true,
    gain: 'Shows ML/CV experience',
    lose: 'Fewer total projects displayed',
  },
  'PROJECTS.CSED_VR': {
    label: 'CSED VR Walkthrough',
    description: 'Show/hide the CSED VR Walkthrough capstone project.',
    performanceImpact: 'Low',
    uxTradeoff: 'Less projects to scroll through',
    recommended: true,
    gain: 'Shows VR/Unity capstone work',
    lose: 'Fewer total projects displayed',
  },
  'PROJECTS.COLLEGE_PREDICTOR': {
    label: 'College Predictor',
    description: 'Show/hide the College Predictor System project.',
    performanceImpact: 'Low',
    uxTradeoff: 'Less projects to scroll through',
    recommended: true,
    gain: 'Shows algorithmic/data work',
    lose: 'Fewer total projects displayed',
  },
  'PROJECTS.CATAPULT': {
    label: 'Catapult',
    description: 'Show/hide the Catapult top-5 finalist project.',
    performanceImpact: 'Low',
    uxTradeoff: 'Less projects to scroll through',
    recommended: true,
    gain: 'Shows competitive engineering work',
    lose: 'Fewer total projects displayed',
  },

  'SIDE_PROJECTS.DARQ_REBORN': {
    label: 'DarQ Reborn',
    description: 'Show/hide the DarQ Reborn card in Side Projects.',
    performanceImpact: 'Low',
    uxTradeoff: 'Less side projects to scroll through',
    recommended: true,
    gain: 'Shows a starred, actively maintained open-source Android tool',
    lose: 'Fewer total side projects displayed',
  },
  'SIDE_PROJECTS.TUFHUB': {
    label: 'TUFHub',
    description: 'Show/hide the TUFHub card in Side Projects.',
    performanceImpact: 'Low',
    uxTradeoff: 'Less side projects to scroll through',
    recommended: true,
    gain: 'Shows a live Chrome extension with its own landing page',
    lose: 'Fewer total side projects displayed',
  },
  'SIDE_PROJECTS.COPYCLIP': {
    label: 'CopyClip',
    description: 'Show/hide the CopyClip card in Side Projects.',
    performanceImpact: 'Low',
    uxTradeoff: 'Less side projects to scroll through',
    recommended: true,
    gain: 'Shows the most recently active personal project',
    lose: 'Fewer total side projects displayed',
  },
  'SIDE_PROJECTS.GRAVITY_BRIDGE': {
    label: 'Gravity Bridge',
    description: 'Show/hide the Gravity Bridge card in Side Projects.',
    performanceImpact: 'Low',
    uxTradeoff: 'Less side projects to scroll through',
    recommended: true,
    gain: 'Shows current AI-agent tooling experience',
    lose: 'Fewer total side projects displayed',
  },
  'SIDE_PROJECTS.CHATALYTICS_PRO': {
    label: 'Chatalytics Pro',
    description: 'Show/hide the Chatalytics Pro card in Side Projects.',
    performanceImpact: 'Low',
    uxTradeoff: 'Less side projects to scroll through',
    recommended: true,
    gain: 'Shows a live, polished frontend product',
    lose: 'Fewer total side projects displayed',
  },
  'SIDE_PROJECTS.MOVIE_ALERT': {
    label: 'Movie Alert',
    description: 'Show/hide the Movie Alert card in Side Projects.',
    performanceImpact: 'Low',
    uxTradeoff: 'Less side projects to scroll through',
    recommended: true,
    gain: 'Shows a clever, finished automation project',
    lose: 'Fewer total side projects displayed',
  },
  'SIDE_PROJECTS.BLUEPRINT_HUB': {
    label: 'Blueprint Hub',
    description: 'Show/hide the Blueprint Hub card in Side Projects.',
    performanceImpact: 'Low',
    uxTradeoff: 'Less side projects to scroll through',
    recommended: true,
    gain: 'Shows technical-writing and documentation depth',
    lose: 'Fewer total side projects displayed',
  },

  'SECTIONS.HERO': {
    label: 'Hero Section',
    description: 'Show/hide the entire hero section (name, roles, profile image, links).',
    performanceImpact: 'Low',
    uxTradeoff: 'Page starts immediately with next section',
    recommended: true,
    gain: 'Visitors see content faster above fold',
    lose: 'No intro/branding at page top',
  },
  'SECTIONS.ABOUT': {
    label: 'About Section',
    description: 'Show/hide the About Me section including skill tags.',
    performanceImpact: 'Low',
    uxTradeoff: 'Shorter page, less personal context',
    recommended: true,
    gain: 'Visitors get straight to experience',
    lose: 'Less personal branding',
  },
  'SECTIONS.EXPERIENCE': {
    label: 'Experience Section',
    description: 'Show/hide the work experience timeline.',
    performanceImpact: 'Low',
    uxTradeoff: 'Core content hidden',
    recommended: true,
    gain: 'Page loads faster with less content',
    lose: 'Visitors miss your career history',
  },
  'SECTIONS.ACHIEVEMENTS': {
    label: 'Achievements Section',
    description: 'Show/hide the milestones and achievements grid.',
    performanceImpact: 'Low',
    uxTradeoff: 'Less social proof on page',
    recommended: true,
    gain: 'Cleaner page with fewer sections',
    lose: 'Removes credibility signals',
  },
  'SECTIONS.PROJECTS': {
    label: 'Projects Section',
    description: 'Show/hide the entire featured projects section.',
    performanceImpact: 'Low',
    uxTradeoff: 'Core portfolio content hidden',
    recommended: true,
    gain: 'Minimal page for specific use cases',
    lose: 'Visitors cannot see your work',
  },
  'SECTIONS.SIDE_PROJECTS': {
    label: 'Side Projects Section',
    description: 'Show/hide the Side Projects & Open Source section.',
    performanceImpact: 'Low',
    uxTradeoff: 'Less personal/open-source work on display',
    recommended: true,
    gain: 'Shows active open-source work beyond the day job',
    lose: 'Visitors miss your side projects',
  },
  'SECTIONS.ARCHIVE': {
    label: 'Archive Section',
    description: 'Show/hide the nostalgic Archive section.',
    performanceImpact: 'Low',
    uxTradeoff: 'Removes the "origin story" card from the page',
    recommended: true,
    gain: 'Gives visitors a before/after narrative arc',
    lose: 'One less section, slightly shorter page',
  },
  'SECTIONS.EDUCATION': {
    label: 'Education Section',
    description: 'Show/hide the education and certifications section.',
    performanceImpact: 'Low',
    uxTradeoff: 'Less background info',
    recommended: true,
    gain: 'Shorter page, faster scroll',
    lose: 'No academic credentials shown',
  },
  'SECTIONS.RECOMMENDATIONS': {
    label: 'Recommendations Section',
    description: 'Show/hide the endorsements / Wall of Love section.',
    performanceImpact: 'Low',
    uxTradeoff: 'Less social proof',
    recommended: true,
    gain: 'Fewer sections, cleaner layout',
    lose: 'No testimonials visible',
  },
  'SECTIONS.FAQ': {
    label: 'FAQ Section',
    description: 'Show/hide the FAQ accordion section with JSON-LD structured data.',
    performanceImpact: 'Low',
    uxTradeoff: 'Adds SEO value but adds one more section to scroll through',
    recommended: true,
    gain: 'Captures long-tail search traffic with rich snippets',
    lose: 'One more section before the footer',
  },
  'SECTIONS.RESUME': {
    label: 'Resume Section',
    description: 'Show/hide the downloadable resume section.',
    performanceImpact: 'Low',
    uxTradeoff: 'No inline resume viewer',
    recommended: true,
    gain: 'Cleaner page bottom',
    lose: 'Visitors cannot download resume directly',
  },
  'SECTIONS.FOOTER': {
    label: 'Footer',
    description: 'Show/hide the page footer with links and copyright.',
    performanceImpact: 'Low',
    uxTradeoff: 'No navigation at page bottom',
    recommended: true,
    gain: 'Minimal page ending',
    lose: 'No social links or copyright in footer',
  },

  'RECOMMENDATIONS_LAYOUT': {
    label: 'Layout Style',
    description: 'Switch between carousel (0) and masonry grid (1) for endorsements.',
    performanceImpact: 'Low',
    uxTradeoff: 'Carousel is compact, masonry is spacious',
    recommended: 0,
    gain: 'Carousel: saves vertical space; Masonry: shows all at once',
    lose: 'Carousel: hides some cards; Masonry: takes more space',
  },
  'ACHIEVEMENTS_LAYOUT': {
    label: 'Achievements Layout',
    description: 'Switch between sticky card stack (0) and timeline (1) for milestones.',
    performanceImpact: 'Low',
    uxTradeoff: 'Stack is compact, timeline is spacious with staggered entrance',
    recommended: 0,
    gain: 'Stack: sticky scroll effect with scale transitions; Timeline: classic alternating card layout with hover effects',
    lose: 'Stack: cards overlap on scroll; Timeline: takes more vertical space',
  },
  'LAZY_LOADING_STRATEGY': {
    label: 'Lazy Strategy',
    description: 'Controls how below-the-fold sections load. 0 = Timer-based (lazyWithPriority): loads sections after a fixed delay based on priority (200ms at pri 0, +50ms per level). Predictable but may load unseen sections. 1 = IntersectionObserver: loads sections only when scrolled within 300px. Saves bandwidth but shows shimmer on first scroll. 2 = Background Preload: preloads all lazy modules after initial render, renders instantly on scroll. Zero shimmer, no CPU cost for unseen sections. Hash-targeted sections preload immediately (no 500ms wait).',
    performanceImpact: 'Medium',
    uxTradeoff: 'Timer: priority-based delays; IO: saves bandwidth; Preload: instant scroll + hash anchors',
    recommended: 2,
    gain: 'Strategy 2: hash-targeted sections load instantly on refresh, zero shimmer on scroll, sections never scrolled to cost zero render time. Strategy 1: lowest bandwidth. Strategy 0: priority-controlled delays.',
    lose: 'Strategy 2: preloads all modules upfront (~20kB extra JS parsed). Strategy 1: visible shimmer on first scroll. Strategy 0: may load sections user never sees.',
  },
  'CUSTOM_CURSOR': {
    label: 'Custom Cursor',
    description: 'Enable/disable the custom animated cursor on desktop.',
    performanceImpact: 'Low',
    uxTradeoff: 'Custom cursor adds personality but hides native cursor',
    recommended: true,
    gain: 'Unique visual flair and personality',
    lose: 'Slightly different feel from OS cursor',
  },
  'CANVAS_BACKGROUND': {
    label: 'Canvas Background',
    description: 'Enable/disable the animated canvas background effect.',
    performanceImpact: 'Medium',
    uxTradeoff: 'Adds visual depth but uses GPU/CPU resources',
    recommended: true,
    gain: 'Eye-catching animated background',
    lose: 'Higher GPU usage, may lag on low-end devices',
  },
  'GITHUB_STATS': {
    label: 'GitHub Stats',
    description: 'Show/hide GitHub contribution stats in the hero section.',
    performanceImpact: 'Low',
    uxTradeoff: 'External API call, may load slowly',
    recommended: true,
    gain: 'Shows GitHub activity at a glance',
    lose: 'External dependency may delay hero rendering',
  },
  'DEV_COMMENTS': {
    label: 'Dev Comments',
    description: 'Show/hide developer comment badges next to section titles.',
    performanceImpact: 'Low',
    uxTradeoff: 'Hidden comments clean up the visual layout',
    recommended: true,
    gain: 'Fun easter egg for technical visitors',
    lose: 'Adds small visual noise to section headers',
  },
  'ANIMATIONS': {
    label: 'Animations',
    description: 'Master toggle for all Framer Motion scroll animations. When ON, individual section animations can be controlled separately below. When OFF, all animations are disabled regardless of individual settings.',
    performanceImpact: 'High',
    uxTradeoff: 'Animations look polished but delay content reveal',
    recommended: true,
    gain: 'Smooth, polished entrance animations; fine-grained control per section',
    lose: 'Master OFF: no animations at all (fastest render). With animations: content appears with slight delay on scroll',
  },
  'CURSOR_EAGER_LOAD': {
    label: 'Cursor Priority',
    description: 'When ON: custom cursor loads eagerly with the main bundle (no chunk delay). Shows native cursor until first frame renders (~16ms). When OFF: cursor loads lazily as separate chunk (saves ~9kB on initial load).',
    performanceImpact: 'Low',
    uxTradeoff: 'Eager: cursor appears faster, no cursor gap. Lazy: saves bandwidth.',
    recommended: true,
    gain: 'Eager: no cursor-less gap, seamless transition from native to custom cursor',
    lose: 'Eager: adds ~9kB to initial bundle. Lazy: CSS chunk hides native cursor before cursor is ready.',
  },
  'ANIM_SECTIONS.HERO': {
    label: 'Hero Animation',
    description: 'Enable/disable entrance animations for the hero section (name, roles, profile image).',
    performanceImpact: 'Low',
    uxTradeoff: 'Animations add polish; disabling makes hero appear immediately',
    recommended: true,
    gain: 'Smooth hero entrance on page load',
    lose: 'Hero content appears with slight delay',
  },
  'ANIM_SECTIONS.ABOUT': {
    label: 'About Animation',
    description: 'Enable/disable scroll-triggered animations for the About section.',
    performanceImpact: 'Low',
    uxTradeoff: 'Animations add visual flow; disabling makes about appear instantly on scroll',
    recommended: true,
    gain: 'Polished scroll reveal for about content',
    lose: 'Content appears without entrance effect',
  },
  'ANIM_SECTIONS.EXPERIENCE': {
    label: 'Experience Animation',
    description: 'Enable/disable scroll-triggered animations for the Experience timeline.',
    performanceImpact: 'Low',
    uxTradeoff: 'Timeline items animate in sequentially',
    recommended: true,
    gain: 'Staggered timeline reveals each role',
    lose: 'All experience cards appear at once',
  },
  'ANIM_SECTIONS.ACHIEVEMENTS': {
    label: 'Achievements Animation',
    description: 'Enable/disable scroll-triggered animations for the Achievements grid.',
    performanceImpact: 'Low',
    uxTradeoff: 'Cards animate in on scroll',
    recommended: true,
    gain: 'Milestone cards appear with staggered entrance',
    lose: 'Cards appear instantly without effect',
  },
  'ANIM_SECTIONS.PROJECTS': {
    label: 'Projects Animation',
    description: 'Enable/disable scroll-triggered animations for the Projects grid.',
    performanceImpact: 'Low',
    uxTradeoff: 'Project cards animate in on scroll',
    recommended: true,
    gain: 'Project cards stagger in smoothly',
    lose: 'Cards appear instantly without entrance',
  },
  'ANIM_SECTIONS.SIDE_PROJECTS': {
    label: 'Side Projects Animation',
    description: 'Enable/disable scroll-triggered animations for the Side Projects grid.',
    performanceImpact: 'Low',
    uxTradeoff: 'Side project cards animate in on scroll',
    recommended: true,
    gain: 'Side project cards stagger in smoothly',
    lose: 'Cards appear instantly without entrance',
  },
  'ANIM_SECTIONS.ARCHIVE': {
    label: 'Archive Animation',
    description: 'Enable/disable the scroll-triggered animation for the Archive card.',
    performanceImpact: 'Low',
    uxTradeoff: 'Archive card fades in on scroll',
    recommended: true,
    gain: 'Smooth reveal for the archive card',
    lose: 'Card appears instantly without effect',
  },
  'ANIM_SECTIONS.EDUCATION': {
    label: 'Education Animation',
    description: 'Enable/disable scroll-triggered animations for the Education and Certifications section.',
    performanceImpact: 'Low',
    uxTradeoff: 'Cert cards and titles animate on scroll',
    recommended: true,
    gain: 'Smooth education section reveal',
    lose: 'Content appears without animation',
  },
  'ANIM_SECTIONS.RECOMMENDATIONS': {
    label: 'Recommendations Animation',
    description: 'Enable/disable scroll-triggered animations for the endorsements section.',
    performanceImpact: 'Low',
    uxTradeoff: 'Endorsement cards animate in on scroll',
    recommended: true,
    gain: 'Polished scroll reveal for testimonials',
    lose: 'Cards appear instantly',
  },
  'ANIM_SECTIONS.RESUME': {
    label: 'Resume Animation',
    description: 'Enable/disable scroll-triggered animations for the Resume section.',
    performanceImpact: 'Low',
    uxTradeoff: 'Resume section fades in on scroll',
    recommended: true,
    gain: 'Smooth resume section appearance',
    lose: 'Resume section appears without effect',
  },
  'PRIORITY.CUSTOM_CURSOR': {
    label: 'Cursor Priority',
    description: 'Loading priority for the custom cursor (0=highest, 9=lowest). Priority 0-1 loads eagerly with the main bundle. Priority 2+ loads as a separate chunk.',
    performanceImpact: 'Low',
    uxTradeoff: 'Higher priority = cursor appears sooner. Lower = saves initial bundle size.',
    recommended: 0,
    gain: 'Priority 0: no cursor gap; Priority 2+: saves ~9kB initial load',
    lose: 'Priority 0: adds ~9kB to main bundle',
  },
  'PRIORITY.CANVAS_BACKGROUND': {
    label: 'Canvas Priority',
    description: 'Loading priority for the animated background (0=highest, 9=lowest).',
    performanceImpact: 'Low',
    uxTradeoff: 'Higher priority = background appears sooner; Lower = defers GPU work',
    recommended: 1,
    gain: 'Priority 0-1: background renders early for visual polish',
    lose: 'Priority 0-1: GPU work starts sooner',
  },
  'ANIM_TYPE.HERO': {
    label: 'Hero Animation Type',
    description: 'Entrance animation style for the hero section.',
    performanceImpact: 'Low',
    uxTradeoff: 'Different animation types create different visual feels',
    recommended: 'fadeInUp',
    gain: 'Control over how hero content enters the page',
    lose: 'Some types may feel too slow or too subtle',
  },
  'ANIM_TYPE.ABOUT': {
    label: 'About Animation Type',
    description: 'Entrance animation style for the About section.',
    performanceImpact: 'Low',
    uxTradeoff: 'Different animation types create different visual feels',
    recommended: 'fadeIn',
    gain: 'Control over how about content reveals on scroll',
    lose: 'Some types may feel too slow or too subtle',
  },
  'ANIM_TYPE.EXPERIENCE': {
    label: 'Experience Animation Type',
    description: 'Entrance animation style for the Experience timeline.',
    performanceImpact: 'Low',
    uxTradeoff: 'Different entrance effects for timeline items',
    recommended: 'fadeInUp',
    gain: 'Control over how timeline items animate in',
    lose: 'Some types may feel too abrupt or too slow',
  },
  'ANIM_TYPE.ACHIEVEMENTS': {
    label: 'Achievements Animation Type',
    description: 'Entrance animation style for the Achievements section.',
    performanceImpact: 'Low',
    uxTradeoff: 'Different entrance effects for milestone cards',
    recommended: 'fadeInUp',
    gain: 'Control over how achievement cards appear',
    lose: 'Some types may feel too abrupt or too slow',
  },
  'ANIM_TYPE.PROJECTS': {
    label: 'Projects Animation Type',
    description: 'Entrance animation style for the Projects grid.',
    performanceImpact: 'Low',
    uxTradeoff: 'Different entrance effects for project cards',
    recommended: 'fadeInUp',
    gain: 'Control over how project cards stagger in',
    lose: 'Some types may feel too abrupt or too slow',
  },
  'ANIM_TYPE.SIDE_PROJECTS': {
    label: 'Side Projects Animation Type',
    description: 'Entrance animation style for the Side Projects grid.',
    performanceImpact: 'Low',
    uxTradeoff: 'Different entrance effects for side project cards',
    recommended: 'fadeInUp',
    gain: 'Control over how side project cards stagger in',
    lose: 'Some types may feel too abrupt or too slow',
  },
  'ANIM_TYPE.ARCHIVE': {
    label: 'Archive Animation Type',
    description: 'Entrance animation style for the Archive card.',
    performanceImpact: 'Low',
    uxTradeoff: 'Different entrance effects for the archive card',
    recommended: 'fadeIn',
    gain: 'Control over how the archive card reveals',
    lose: 'Some types may feel too abrupt or too slow',
  },
  'ANIM_TYPE.EDUCATION': {
    label: 'Education Animation Type',
    description: 'Entrance animation style for the Education section.',
    performanceImpact: 'Low',
    uxTradeoff: 'Different entrance effects for education columns',
    recommended: 'fadeIn',
    gain: 'Control over how education content reveals',
    lose: 'Some types may feel too abrupt or too slow',
  },
  'ANIM_TYPE.RECOMMENDATIONS': {
    label: 'Recommendations Animation Type',
    description: 'Entrance animation style for the Recommendations section.',
    performanceImpact: 'Low',
    uxTradeoff: 'Different entrance effects for testimonial cards',
    recommended: 'fadeInUp',
    gain: 'Control over how endorsement cards appear',
    lose: 'Some types may feel too abrupt or too slow',
  },
  'ANIM_TYPE.RESUME': {
    label: 'Resume Animation Type',
    description: 'Entrance animation style for the Resume section.',
    performanceImpact: 'Low',
    uxTradeoff: 'Different entrance effects for the resume card',
    recommended: 'fadeInUp',
    gain: 'Control over how the resume card appears',
    lose: 'Some types may feel too abrupt or too slow',
  },
  'ANIM_SPEED.HERO': {
    label: 'Hero Animation Speed',
    description: 'Duration in seconds for the hero entrance animation (0.2 - 2.0).',
    performanceImpact: 'Low',
    uxTradeoff: 'Faster feels snappier, slower feels more dramatic',
    recommended: 0.5,
    gain: 'Fine-tune the pace of hero entrance',
    lose: 'Too fast: barely noticeable; Too slow: impatient',
  },
  'ANIM_SPEED.ABOUT': {
    label: 'About Animation Speed',
    description: 'Duration in seconds for the About section animation (0.2 - 2.0).',
    performanceImpact: 'Low',
    uxTradeoff: 'Faster feels snappier, slower feels more dramatic',
    recommended: 0.6,
    gain: 'Fine-tune the pace of about section reveal',
    lose: 'Too fast: barely noticeable; Too slow: impatient',
  },
  'ANIM_SPEED.EXPERIENCE': {
    label: 'Experience Animation Speed',
    description: 'Duration in seconds for Experience timeline animation (0.2 - 2.0).',
    performanceImpact: 'Low',
    uxTradeoff: 'Faster feels snappier, slower feels more dramatic',
    recommended: 0.6,
    gain: 'Fine-tune the pace of timeline item entrance',
    lose: 'Too fast: barely noticeable; Too slow: impatient',
  },
  'ANIM_SPEED.ACHIEVEMENTS': {
    label: 'Achievements Animation Speed',
    description: 'Duration in seconds for Achievements animation (0.2 - 2.0).',
    performanceImpact: 'Low',
    uxTradeoff: 'Faster feels snappier, slower feels more dramatic',
    recommended: 0.5,
    gain: 'Fine-tune the pace of milestone card entrance',
    lose: 'Too fast: barely noticeable; Too slow: impatient',
  },
  'ANIM_SPEED.PROJECTS': {
    label: 'Projects Animation Speed',
    description: 'Duration in seconds for Projects animation (0.2 - 2.0).',
    performanceImpact: 'Low',
    uxTradeoff: 'Faster feels snappier, slower feels more dramatic',
    recommended: 0.5,
    gain: 'Fine-tune the pace of project card entrance',
    lose: 'Too fast: barely noticeable; Too slow: impatient',
  },
  'ANIM_SPEED.SIDE_PROJECTS': {
    label: 'Side Projects Animation Speed',
    description: 'Duration in seconds for Side Projects animation (0.2 - 2.0).',
    performanceImpact: 'Low',
    uxTradeoff: 'Faster feels snappier, slower feels more dramatic',
    recommended: 0.5,
    gain: 'Fine-tune the pace of side project card entrance',
    lose: 'Too fast: barely noticeable; Too slow: impatient',
  },
  'ANIM_SPEED.ARCHIVE': {
    label: 'Archive Animation Speed',
    description: 'Duration in seconds for the Archive card animation (0.2 - 2.0).',
    performanceImpact: 'Low',
    uxTradeoff: 'Faster feels snappier, slower feels more dramatic',
    recommended: 0.6,
    gain: 'Fine-tune the pace of the archive card reveal',
    lose: 'Too fast: barely noticeable; Too slow: impatient',
  },
  'ANIM_SPEED.EDUCATION': {
    label: 'Education Animation Speed',
    description: 'Duration in seconds for Education animation (0.2 - 2.0).',
    performanceImpact: 'Low',
    uxTradeoff: 'Faster feels snappier, slower feels more dramatic',
    recommended: 0.6,
    gain: 'Fine-tune the pace of education section reveal',
    lose: 'Too fast: barely noticeable; Too slow: impatient',
  },
  'ANIM_SPEED.RECOMMENDATIONS': {
    label: 'Recommendations Animation Speed',
    description: 'Duration in seconds for Recommendations animation (0.2 - 2.0).',
    performanceImpact: 'Low',
    uxTradeoff: 'Faster feels snappier, slower feels more dramatic',
    recommended: 0.5,
    gain: 'Fine-tune the pace of endorsement card entrance',
    lose: 'Too fast: barely noticeable; Too slow: impatient',
  },
  'ANIM_SPEED.RESUME': {
    label: 'Resume Animation Speed',
    description: 'Duration in seconds for Resume animation (0.2 - 2.0).',
    performanceImpact: 'Low',
    uxTradeoff: 'Faster feels snappier, slower feels more dramatic',
    recommended: 0.5,
    gain: 'Fine-tune the pace of resume card appearance',
    lose: 'Too fast: barely noticeable; Too slow: impatient',
  },
  'MA_HOVER_EFFECT': {
    label: 'MA Hover Effect',
    description: 'Choose the hover effect for the MA logo in the navbar. 0 = None, 1 = Coder Peek (fair skin emoji peeks from behind the M), 2 = Wave Ripple (concentric rings expand outward on hover).',
    performanceImpact: 'Low',
    uxTradeoff: 'Effects add personality; None is minimal and distraction-free.',
    recommended: 1,
    gain: 'Coder Peek: playful hidden easter egg; Ripple: subtle visual feedback',
    lose: 'Coder Peek: adds slight visual noise; Ripple: brief CSS animation cost',
  },
  'DEV_MODE_TIMER': {
    label: 'Dev Mode Timer',
    description: 'Auto-lock timer duration for Developer Mode. Once unlocked, dev mode auto-locks after this many minutes. Timer resets on re-lock. 0.5 = 30 seconds.',
    performanceImpact: 'Low',
    uxTradeoff: 'Shorter timer auto-locks faster (secure). Longer timer lets you inspect settings without re-unlocking.',
    recommended: 15,
    gain: 'Shorter: dev mode cleans up faster; Longer: more time to explore debug settings',
    lose: 'Shorter: may lock while actively using; Longer: dev mode may stay on after walking away',
  },
  'EXPERIENCE_SPRING_ANIM': {
    label: 'Experience Spring',
    description: 'ON (default): Spring physics (stiffness: 350, damping: 15) for timeline card entrance - bouncy, organic feel. OFF: Falls back to tween (easeOut) - smooth deceleration. NOTE: Refresh the page after toggling - animation only plays once per scroll.',
    performanceImpact: 'Low',
    uxTradeoff: 'Spring: bouncy overshoot; Tween: clean deceleration',
    recommended: true,
    gain: 'Spring adds organic, premium bounce to timeline cards',
    lose: 'Spring may overshoot if values are aggressive',
  },
}
